// Toolbar component for sort, group, view modes with premium glassmorphism styling
import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { GroupOption, SortOption, ViewMode } from '../entities/CatchesPageTypes';
import { BottomSheet } from './BottomSheet';

interface ToolbarProps {
  groupBy: GroupOption;
  onGroupByChange: (option: GroupOption) => void;
  sortBy: SortOption;
  onSortByChange: (option: SortOption) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  selectionMode: boolean;
  onToggleSelection: () => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  groupBy,
  onGroupByChange,
  sortBy,
  onSortByChange,
  viewMode,
  onViewModeChange,
  selectionMode,
  onToggleSelection
}) => {
  const { t } = useLanguage();
  const [showGroupSort, setShowGroupSort] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const getGroupLabel = (option: GroupOption) => {
    switch (option) {
      case 'date': return `📅 ${t.date}`;
      case 'area': return `📍 ${t.area}`;
      case 'species': return `🐟 ${t.species}`;
      case 'session': return `🎣 ${t.session}`;
      case 'none': return t.none;
    }
  };
  
  const getSortLabel = (option: SortOption) => {
    switch (option) {
      case 'newest': return `⏰ ${t.newest}`;
      case 'oldest': return `⏰ ${t.oldest}`;
      case 'weight-desc': return `⚖️ ${t.weight}↓`;
      case 'length-desc': return `📏 ${t.length}↓`;
    }
  };
  
  // Mobile version
  if (isMobile) {
    return (
      <>
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '12px 16px',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <button 
            onClick={() => setShowGroupSort(true)}
            style={{
              flex: 1,
              padding: '10px',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {getGroupLabel(groupBy)} • {getSortLabel(sortBy)}
          </button>
          
          <button
            onClick={onToggleSelection}
            style={{
              padding: '10px 14px',
              background: selectionMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.08)',
              border: `1px solid ${selectionMode ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '8px',
              color: 'white',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {selectionMode ? '✓' : '☐'}
          </button>
        </div>
        
        <BottomSheet isOpen={showGroupSort} onClose={() => setShowGroupSort(false)} title={`${t.groupBy} & ${t.sortBy}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <h4 style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontSize: '14px', 
                marginBottom: '12px', 
                margin: 0 
              }}>
                {t.groupBy}:
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '12px' }}>
                {(['date', 'area', 'species', 'session', 'none'] as GroupOption[]).map(option => (
                  <button
                    key={option}
                    onClick={() => { onGroupByChange(option); setShowGroupSort(false); }}
                    style={{
                      padding: '12px',
                      background: groupBy === option ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${groupBy === option ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {getGroupLabel(option)}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h4 style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontSize: '14px', 
                marginBottom: '12px', 
                margin: 0 
              }}>
                {t.sortBy}:
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '12px' }}>
                {(['newest', 'oldest', 'weight-desc', 'length-desc'] as SortOption[]).map(option => (
                  <button
                    key={option}
                    onClick={() => { onSortByChange(option); setShowGroupSort(false); }}
                    style={{
                      padding: '12px',
                      background: sortBy === option ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${sortBy === option ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {getSortLabel(option)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </BottomSheet>
      </>
    );
  }
  
  // Desktop version
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
      padding: '12px 16px',
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(12px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      alignItems: 'center'
    }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>{t.groupBy}:</span>
        {(['date', 'area', 'species', 'session', 'none'] as GroupOption[]).map(option => (
          <button
            key={option}
            onClick={() => onGroupByChange(option)}
            style={{
              padding: '6px 12px',
              background: groupBy === option ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${groupBy === option ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.15)'}`,
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease'
            }}
          >
            {getGroupLabel(option)}
          </button>
        ))}
      </div>
      
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>{t.sortBy}:</span>
        {(['newest', 'oldest', 'weight-desc', 'length-desc'] as SortOption[]).map(option => (
          <button
            key={option}
            onClick={() => onSortByChange(option)}
            style={{
              padding: '6px 12px',
              background: sortBy === option ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${sortBy === option ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.15)'}`,
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease'
            }}
          >
            {getSortLabel(option)}
          </button>
        ))}
      </div>
      
      <button
        onClick={onToggleSelection}
        style={{
          padding: '6px 14px',
          background: selectionMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${selectionMode ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.15)'}`,
          borderRadius: '6px',
          color: 'white',
          fontSize: '13px',
          cursor: 'pointer',
          marginLeft: 'auto',
          transition: 'all 0.2s ease'
        }}
      >
        {selectionMode ? '✓ ' : '☐ '}{t.selectMultiple || 'Select'}
      </button>
    </div>
  );
};
